import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  Briefcase,
  Building2,
  FileCheck2,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Moon,
  Search,
  ScrollText,
  Settings as SettingsIcon,
  Sun,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { getSession, signOut } from '@/lib/auth';
import { loadSettings, subscribeSettings, type UserSettings } from '@/lib/settings';
import { getTheme, subscribeTheme, toggleTheme, type Theme } from '@/lib/theme';
import { NotificationBell } from '@/components/NotificationBell';
import { PauseBanner } from '@/components/PauseBanner';
import { WorkspaceSwitcher } from '@/components/WorkspaceSwitcher';
import { CreditBalanceChip } from '@/components/CreditBalanceChip';

function initialsFromName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '·';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

function useSettings(): UserSettings {
  return useSyncExternalStore(subscribeSettings, loadSettings, loadSettings);
}

function useTheme(): Theme {
  return useSyncExternalStore(subscribeTheme, getTheme, getTheme);
}

type NavGroup = 'Workspace' | 'Operations' | 'Account';

type NavItem = {
  to: string;
  label: string;
  end?: boolean;
  icon: LucideIcon;
  group: NavGroup;
};

const NAV: NavItem[] = [
  { to: '/', label: 'Overview', end: true, icon: LayoutDashboard, group: 'Workspace' },
  { to: '/rlps', label: 'Solicitations', icon: ScrollText, group: 'Workspace' },
  { to: '/portfolio', label: 'Portfolio', icon: Briefcase, group: 'Workspace' },
  { to: '/represented', label: 'Represented', icon: Building2, group: 'Workspace' },
  { to: '/messages', label: 'Messages', icon: MessageSquare, group: 'Workspace' },
  { to: '/forms', label: 'Forms', icon: FileCheck2, group: 'Workspace' },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, group: 'Account' },
];

function groupBy<T, K extends string>(list: T[], key: (t: T) => K): Record<K, T[]> {
  const out = {} as Record<K, T[]>;
  for (const item of list) {
    const k = key(item);
    if (!out[k]) out[k] = [];
    out[k]!.push(item);
  }
  return out;
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const settings = useSettings();
  const theme = useTheme();

  // Route-level guard: defense in depth on top of the Pages Functions
  // access middleware. Renders /auth if the user has no local session.
  // The production surface is additionally gated by Cloudflare Access.
  if (!getSession()) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }
  const org = settings.profile;
  const orgName = org.organization_name || 'Workspace';
  const orgTagline = org.organization_tagline || '';
  const orgLogo = org.organization_logo_data_url;
  const orgInitials = initialsFromName(orgName);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const grouped = groupBy(NAV, (n) => n.group);

  const handleSignOut = () => {
    signOut();
    navigate('/auth');
  };

  return (
    <div className="min-h-full">
      {/* Sidebar — fixed on all breakpoints; slides in/out on mobile */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-[260px] flex-col border-r border-sidebar-border bg-sidebar transition-transform lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Brand */}
        <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-5">
          <div className="flex items-center gap-2.5">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 text-white shadow-card">
              <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
                <path d="M4 7h16M4 12h10M4 17h7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <div className="text-[0.9375rem] font-bold tracking-tight text-white">RLPScout</div>
              <div className="text-[0.625rem] font-medium uppercase tracking-[0.1em] text-sidebar-ink-dim">
                Intelligence
              </div>
            </div>
          </div>
          <button
            type="button"
            className="text-sidebar-ink-muted hover:text-white lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav */}
        <nav
          aria-label="Primary"
          data-testid="primary-nav"
          className="flex flex-1 flex-col gap-6 overflow-y-auto px-3 py-5 scroll-elegant"
        >
          {(['Workspace', 'Operations', 'Account'] as const).map((section) => (
            <div key={section} className="flex flex-col gap-1">
              <div className="px-3 pb-1 text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-sidebar-ink-dim">
                {section}
              </div>
              {grouped[section]?.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn('nav-item', isActive && 'nav-item-active')
                    }
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    <span className="flex-1">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User card + sign out */}
        <div className="flex flex-col gap-2 border-t border-sidebar-border p-4">
          <button
            type="button"
            onClick={() => toggleTheme()}
            className="flex w-full items-center gap-2.5 rounded-lg border border-sidebar-border bg-transparent px-3 py-2 text-[0.8125rem] font-semibold text-sidebar-ink-muted transition-colors hover:border-brand-500/40 hover:bg-brand-500/10 hover:text-white"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
          </button>
          <div className="flex items-center gap-3 rounded-lg bg-sidebar-raised px-3 py-2.5">
            {orgLogo ? (
              <img
                src={orgLogo}
                alt={orgName}
                className="h-9 w-9 shrink-0 rounded-full bg-white object-contain p-0.5 ring-1 ring-sidebar-border"
              />
            ) : (
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[0.75rem] font-bold text-white">
                {orgInitials}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white">{orgName}</div>
              {orgTagline && (
                <div className="truncate text-[0.6875rem] text-sidebar-ink-dim">{orgTagline}</div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full items-center gap-2.5 rounded-lg border border-sidebar-border bg-transparent px-3 py-2 text-[0.8125rem] font-semibold text-sidebar-ink-muted transition-colors hover:border-danger/40 hover:bg-danger/10 hover:text-white"
            data-testid="signout-sidebar"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Mobile scrim */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink/40 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      {/* Right side — offset by sidebar width on lg+ */}
      <div className="flex min-h-full min-w-0 flex-col lg:pl-[260px]">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-rule bg-surface/90 px-4 backdrop-blur-md sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            className="grid h-9 w-9 place-items-center rounded-lg border border-rule text-ink-muted hover:border-rule-strong hover:text-ink lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="relative max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" />
            <input
              type="search"
              placeholder="Search solicitations, agencies, addresses…"
              className="h-10 w-full rounded-lg border border-rule bg-surface-sunk pl-9 pr-3 text-sm text-ink placeholder:text-ink-dim focus:border-brand-500 focus:bg-surface focus:outline-none focus:ring-4 focus:ring-brand-500/10"
            />
          </div>

          <div className="flex items-center gap-2">
            <WorkspaceSwitcher />
            <CreditBalanceChip />
            <NotificationBell />
            <div className="ml-1 hidden items-center gap-3 border-l border-rule pl-3 sm:flex">
              <div className="text-right">
                <div className="text-[0.8125rem] font-semibold text-ink">George Mogga</div>
                <div className="text-[0.6875rem] text-ink-muted">Operator · Admin</div>
              </div>
              <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-[0.75rem] font-bold text-white">
                GM
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden px-4 py-10 sm:px-6 lg:px-10 lg:py-12">
          <div className="w-full">
            <PauseBanner />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
